import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Home, Paintbrush, SprayCan, Palette, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';

const iconMap = {
  'home': Home,
  'paintbrush': Paintbrush,
  'spray-can': SprayCan,
  'palette': Palette,
  'wrench': Wrench
};

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('Tous');

  const categories = ['Tous', 'Ravalement et rénovation', 'Isolation et performance énergétique', 'Entretien et protection'];
  
  const filteredServices = selectedCategory === 'Tous' 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Nos services
            </h1>
            <p className="text-xl text-gray-600">
              Des solutions complètes et professionnelles pour tous vos travaux de façade
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-cyan-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedCategory === 'Tous' ? 'Tous nos services' : selectedCategory}
              <span className="text-cyan-600 ml-3">({filteredServices.length})</span>
            </h2>
          </div>
          <div className="space-y-20">
            {filteredServices.map((service, index) => (
            <div key={service.id}>
              <div className="max-w-4xl">
                <div className="inline-block bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-xs font-semibold mb-4">
                  {service.category}
                </div>
                <div className="bg-gradient-to-br from-cyan-500 to-teal-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  {React.createElement(iconMap[service.icon] || Home, {
                    className: "w-8 h-8 text-white"
                  })}
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  {service.description}
                </p>
                <div className="space-y-3">
                  {service.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Process Details for Réparation et entretien */}
              {service.detailedProcess && service.processImages && (
                <div className="mt-12 bg-gradient-to-br from-gray-50 to-cyan-50 rounded-2xl p-8 border-2 border-cyan-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <div className="bg-cyan-600 w-2 h-8 rounded-full mr-4"></div>
                    Notre processus de réparation de fissures
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-8">
                    {service.detailedProcess}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {service.processImages.map((img, imgIndex) => (
                      <div key={imgIndex} className="overflow-hidden rounded-xl shadow-lg">
                        <img
                          src={img}
                          alt={`Processus de réparation`}
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Service Images for Peinture et enduits décoratifs */}
              {service.serviceImages && (
                <div className="mt-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Nos réalisations
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {service.serviceImages.map((img, imgIndex) => (
                      <div key={imgIndex} className="overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
                        <img
                          src={img}
                          alt={`Réalisation ${imgIndex + 1}`}
                          className="w-full h-64 object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre processus
            </h2>
            <p className="text-lg text-gray-600">
              Un accompagnement sur mesure à chaque étape
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Contact', description: 'Vous nous contactez pour un devis gratuit' },
              { step: '02', title: 'Visite', description: 'Nous visitons votre bien et évaluons les travaux' },
              { step: '03', title: 'Devis', description: 'Nous vous proposons un devis détaillé et transparent' },
              { step: '04', title: 'Réalisation', description: 'Nous réalisons les travaux dans les délais convenus' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="bg-gradient-to-br from-cyan-600 to-teal-600 text-white text-2xl font-bold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-all">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 via-cyan-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Un projet en tête ?
          </h2>
          <p className="text-xl text-cyan-50 mb-8">
            Demandez votre devis gratuit et sans engagement
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 shadow-xl">
              Obtenir un devis gratuit
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;